import _ from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { useState } from 'react';
import styled from 'styled-components';

import { CheckCircleTwoTone } from '@ant-design/icons';
import { Col, Input, notification, Row, Spin, Tooltip } from 'antd';

import { PitstopModal } from 'shared';
import {
  Button,
  Card,
  ContactLink,
  DashLine,
  ErrorField,
} from 'v3/components/pitstop';

import { AppStore, IntegrationStore, ShopStore } from 'stores';
import GeotabDatabaseInfo from 'v3/assets/geotab-database-info.png';
import GeotabUrlInfo from 'v3/assets/geotab-url-info.png';
import QuestionCirclePNG from 'v3/assets/question-circle.png';

const StyledModal = styled(PitstopModal)`
  && {
    .ant-modal-content {
      padding: 0px;
    }
  }
`;

const Container = styled.div`
  text-align: center;

  h2 {
    font-weight: 600;
    font-size: 2.4rem;
    line-height: 3rem;
  }

  h3 {
    font-weight: 600;
    font-size: 1.2rem;
    line-height: 1.8rem;
    margin-top: 10rem;
  }

  p {
    font-size: 1rem;
    line-height: 1.2rem;
  }

  label {
    font-weight: 600;
    display: block;
    text-align: left;
    width: 100%;
  }

  input {
    margin: 1.2vh 0vh;
  }
`;

const QuestionCircle = styled.img`
  width: 16px;
  height: 16px;
  margin: auto;
`;

const GeotabIntegrationForm = ({ id }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    database: '',
    url: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const modalId = id || 'geotab-integration-form';

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getError = (type, value) => {
    if (_.isEmpty(value) || _.isNil(value)) {
      return (
        <ErrorField style={{ textAlign: 'left' }}>
          {type} is required!
        </ErrorField>
      );
    }
    return null;
  };

  const onConnect = async () => {
    setIsSubmitted(true);
    const { username, password, database, url } = formData;

    if (!username || !password || !database || !url) return;

    try {
      notification.open({
        key: 'geotab-integration',
        description: (
          <>
            <Spin size="large" style={{ marginRight: '0.6rem' }} />
            Adding Geotab integration...
          </>
        ),
        duration: 0,
      });

      await IntegrationStore.createGeotabIntegrationRecord({
        shopId: ShopStore.currentShop.id,
        username,
        password,
        alias: username,
        database,
        url,
        defaultMigrationStartTimestamp: moment().startOf('date'),
      });

      setStep(3);
    } catch (err) {
      AppStore.addError(err.message);
    } finally {
      notification.destroy('geotab-integration');
    }
  };

  return (
    <StyledModal id={modalId} style={{ top: '8vw' }} width={1030} footer={null}>
      <Container>
        <Card>
          <Row
            type="flex"
            justify="center"
            style={{ height: '100%', padding: '50px 0px' }}
          >
            {step === 1 && (
              <Col span={16}>
                <h2>Connect Your Geotab Plus Account</h2>
                <h3>
                  Before you start please ensure you are using Google Chrome or
                  Firefox browser
                </h3>
                <p style={{ marginBottom: '10rem' }}>
                  This is to ensure a hiccup-free integration
                </p>
                <Button
                  style={{ minWidth: '10rem' }}
                  onClick={() => setStep(2)}
                >
                  Get Started
                </Button>
              </Col>
            )}

            {step === 2 && (
              <Col span={16}>
                <h2>Connect To Geotab</h2>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <label>Username</label>
                    <Input
                      name="username"
                      placeholder="Username"
                      onChange={onChange}
                    />
                    {isSubmitted && getError('username', formData.username)}
                  </Col>

                  <Col span={12}>
                    <label>Password</label>
                    <Input
                      name="password"
                      type="password"
                      placeholder="Password"
                      onChange={onChange}
                    />
                    {isSubmitted && getError('password', formData.password)}
                  </Col>
                </Row>

                <DashLine style={{ margin: '4vh 0vh' }} />

                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <div style={{ display: 'flex' }}>
                      <label>Database</label>
                      <Tooltip
                        title={<img src={GeotabDatabaseInfo} alt="" />}
                        placement="bottomRight"
                      >
                        <QuestionCircle
                          src={QuestionCirclePNG}
                          alt="question-circle"
                        />
                      </Tooltip>
                    </div>
                    <Input
                      name="database"
                      placeholder="www.geotab.com/database/"
                      onChange={onChange}
                    />
                    {isSubmitted && getError('database', formData.database)}
                  </Col>

                  <Col span={12}>
                    <div style={{ display: 'flex' }}>
                      <label>URL</label>
                      <Tooltip
                        title={<img src={GeotabUrlInfo} alt="" />}
                        placement="bottomRight"
                      >
                        <QuestionCircle
                          src={QuestionCirclePNG}
                          alt="question-circle"
                        />
                      </Tooltip>
                    </div>
                    <Input
                      name="url"
                      placeholder="www.geotab.com/"
                      onChange={onChange}
                    />
                    {isSubmitted && getError('url', formData.url)}
                  </Col>
                </Row>

                <Button
                  style={{ minWidth: '10rem', marginTop: '10vh' }}
                  onClick={onConnect}
                >
                  Connect
                </Button>
              </Col>
            )}

            {step === 3 && (
              <Col
                span={16}
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  flexDirection: 'column',
                }}
              >
                <CheckCircleTwoTone
                  twoToneColor="#52c41a"
                  style={{ fontSize: '64px' }}
                />
                <h3 style={{ marginTop: '1vh' }}>
                  Your Geotab fleet is connected
                </h3>
                <Button
                  style={{ minWidth: '10rem', marginTop: '2vh' }}
                  onClick={() => {
                    AppStore.openModals.set(modalId, false);
                    AppStore.openModals.set('ADD_INTEGRATION_MODAL_ID', false);
                    AppStore.openModals.set('PROFILE_FORM_ID', true);
                  }}
                >
                  Complete Profile
                </Button>
              </Col>
            )}

            <Col span={16}>
              <ContactLink />
            </Col>
          </Row>
        </Card>
      </Container>
    </StyledModal>
  );
};

GeotabIntegrationForm.propTypes = {
  id: PropTypes.string,
};

export default GeotabIntegrationForm;
